import { useEffect, useState, useMemo, useCallback } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Bell, ExternalLink, Calendar, Users, RefreshCw, Sparkles, ArrowRight, MapPin, AlertCircle, GraduationCap } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const SITE = "https://vedantiacademy.com";

const CATEGORIES = [
  { id: "all", label: "All", accent: "from-vedanti-navy to-blue-600" },
  { id: "banking", label: "Banking", accent: "from-blue-600 to-vedanti-navy" },
  { id: "ssc", label: "SSC", accent: "from-emerald-600 to-green-500" },
  { id: "railway", label: "Railway", accent: "from-amber-600 to-vedanti-orange" },
  { id: "mba", label: "MBA / CET", accent: "from-purple-600 to-pink-500" },
  { id: "insurance", label: "Insurance", accent: "from-cyan-600 to-teal-500" },
  { id: "others", label: "Other Govt", accent: "from-rose-600 to-red-500" },
];

const fmtDate = (iso) => {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  } catch { return iso; }
};

const daysLeft = (iso) => {
  if (!iso) return null;
  try {
    const target = new Date(iso);
    if (Number.isNaN(target.getTime())) return null;
    const diff = Math.ceil((target - new Date()) / (1000 * 60 * 60 * 24));
    return diff >= 0 ? diff : null;
  } catch { return null; }
};

export default function NotificationsPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");
  const [activeCat, setActiveCat] = useState("all");
  const [lastFetched, setLastFetched] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/notifications?active_only=true&limit=100`);
      if (!res.ok) throw new Error("Network error");
      const data = await res.json();
      setItems(data);
      setLastFetched(new Date());
      setError("");
    } catch (e) {
      setError("Couldn't load notifications. Please refresh.");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  // Initial load + auto-refresh every 5 minutes
  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const manualRefresh = async () => {
    setRefreshing(true);
    await fetchData();
  };

  const filtered = useMemo(() => {
    if (activeCat === "all") return items;
    return items.filter((i) => i.category === activeCat);
  }, [items, activeCat]);

  // JSON-LD for ItemList (SEO)
  const itemListLd = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: "Vedanti Academy — Latest Job Notifications & Exam Alerts",
    numberOfItems: items.length,
    itemListElement: items.slice(0, 30).map((n, i) => ({
      "@type": "ListItem",
      position: i + 1,
      url: n.apply_url || SITE,
      name: n.title,
    })),
  };

  return (
    <div data-testid="notifications-page">
      <Helmet>
        <title>Latest Exam & Job Notifications 2026 | Vedanti</title>
        <meta name="description" content="Stay updated with the latest Banking, SSC, Railway, MBA CET, LIC, NIACL and government job notifications for 2026 & 2027. Auto-refreshed every 12 hours by Vedanti Academy." />
        <link rel="canonical" href={`${SITE}/notifications`} />
        <meta property="og:title" content="Latest Job Notifications & Exam Alerts 2026 | Vedanti Academy" />
        <meta property="og:description" content="Banking, SSC, Railway, MBA CET, LIC, NIACL and government exam notifications — vacancies, last dates and apply links." />
        <script type="application/ld+json">{JSON.stringify(itemListLd)}</script>
      </Helmet>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-vedanti-navy"></div>
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_rgba(249,115,22,0.2)_0%,_transparent_55%)]"></div>
        <div className="absolute bottom-0 inset-x-0 h-1.5 bg-gradient-to-r from-vedanti-orange via-vedanti-mango to-vedanti-orange"></div>
        <div className="container-main py-12 md:py-16 relative z-10">
          <div className="flex items-center gap-2 mb-3">
            <Bell size={16} className="text-vedanti-mango" />
            <span className="text-vedanti-mango text-sm font-bold uppercase tracking-wider">Live Notifications</span>
            {lastFetched && (
              <span className="text-slate-400 text-xs ml-3 hidden sm:inline">
                Last updated {lastFetched.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}
              </span>
            )}
          </div>
          <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white leading-tight">
            Latest Job <span className="text-transparent bg-clip-text bg-gradient-to-r from-vedanti-orange to-vedanti-mango">Notifications</span> & Exam Alerts
          </h1>
          <p className="text-slate-300 mt-4 text-base md:text-lg max-w-3xl">
            Stay ahead with auto-updated Banking, SSC, Railway, MBA CET, LIC, NIACL and other government exam notifications — vacancies, last dates and direct apply links.
          </p>
          <div className="mt-6 flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={manualRefresh}
              disabled={refreshing || loading}
              className="inline-flex items-center gap-2 bg-vedanti-orange hover:bg-orange-600 text-white px-5 py-2.5 rounded-xl font-semibold text-sm transition-all shadow-lg disabled:opacity-60"
              data-testid="notifications-refresh-btn"
            >
              <RefreshCw size={15} className={refreshing ? "animate-spin" : ""} />
              {refreshing ? "Refreshing…" : "Refresh now"}
            </button>
            <Link to="/demo-class" className="inline-flex items-center gap-2 border-2 border-white/30 text-white hover:bg-white/10 px-5 py-2.5 rounded-xl font-semibold text-sm transition-all">
              <Sparkles size={15} /> Free Demo for Selected Exam
            </Link>
          </div>
        </div>
      </section>

      {/* Category Filter */}
      <section className="bg-white border-b border-slate-100 sticky top-16 z-30 backdrop-blur-md">
        <div className="container-main py-4 overflow-x-auto">
          <div className="flex gap-2 min-w-max" data-testid="notifications-cats">
            {CATEGORIES.map((c) => {
              const count = c.id === "all" ? items.length : items.filter((i) => i.category === c.id).length;
              const isActive = activeCat === c.id;
              return (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => setActiveCat(c.id)}
                  className={`px-4 py-2 rounded-full text-sm font-bold transition-all whitespace-nowrap ${isActive ? `bg-gradient-to-r ${c.accent} text-white shadow-md` : "bg-slate-100 text-slate-600 hover:text-vedanti-navy"}`}
                  data-testid={`notifications-cat-${c.id}`}
                >
                  {c.label} <span className="ml-1 opacity-70">({count})</span>
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* List */}
      <section className="section-padding bg-gradient-to-b from-white to-slate-50">
        <div className="container-main">
          {loading && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="notifications-loading">
              {[...Array(6)].map((_, i) => (
                <div key={`notif-skeleton-${i}`} className="bg-white rounded-2xl p-5 border border-slate-100 animate-pulse">
                  <div className="h-3 w-16 bg-slate-200 rounded mb-3"></div>
                  <div className="h-5 w-3/4 bg-slate-200 rounded mb-2"></div>
                  <div className="h-3 w-1/2 bg-slate-200 rounded mb-4"></div>
                  <div className="h-10 bg-slate-200 rounded"></div>
                </div>
              ))}
            </div>
          )}

          {!loading && error && (
            <div className="bg-red-50 border border-red-100 text-red-700 rounded-xl p-4 flex items-center gap-3" data-testid="notifications-error">
              <AlertCircle size={18} />
              <span className="text-sm font-medium">{error}</span>
            </div>
          )}

          {!loading && !error && filtered.length === 0 && (
            <p className="text-center text-slate-500 py-12" data-testid="notifications-empty">
              No active notifications in this category right now. Check back soon — new alerts auto-refresh every 12 hours.
            </p>
          )}

          {!loading && !error && filtered.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filtered.map((n) => {
                const cat = CATEGORIES.find((c) => c.id === n.category) || CATEGORIES[0];
                const dl = daysLeft(n.last_date);
                const isUrgent = dl !== null && dl <= 7;
                return (
                  <article
                    key={n.id}
                    className="bg-white rounded-2xl border border-slate-100 hover:border-vedanti-orange/40 shadow-sm hover:shadow-xl transition-all overflow-hidden flex flex-col"
                    data-testid={`notification-${n.id}`}
                  >
                    {/* Top accent + category */}
                    <div className={`h-1 bg-gradient-to-r ${cat.accent}`}></div>
                    <div className="p-5 flex-1 flex flex-col">
                      <div className="flex items-center justify-between mb-2 gap-2">
                        <span className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full bg-gradient-to-r ${cat.accent} text-white`}>
                          {cat.label}
                        </span>
                        {isUrgent && (
                          <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full bg-red-500 text-white animate-pulse">
                            <AlertCircle size={10} /> {dl} day{dl !== 1 ? "s" : ""} left
                          </span>
                        )}
                      </div>

                      <h3 className="font-heading font-bold text-vedanti-navy text-base leading-snug mb-1.5">
                        {n.title}
                      </h3>
                      {n.summary && (
                        <p className="text-slate-500 text-xs leading-relaxed line-clamp-3 mb-3">{n.summary}</p>
                      )}

                      <div className="grid grid-cols-2 gap-2 mt-auto mb-3 text-xs">
                        {n.vacancies && (
                          <div className="bg-slate-50 rounded-lg p-2">
                            <p className="text-[10px] uppercase font-semibold text-slate-500 tracking-wider flex items-center gap-1"><Users size={10} />Vacancies</p>
                            <p className="font-bold text-vedanti-navy mt-0.5 text-sm">{n.vacancies}</p>
                          </div>
                        )}
                        {n.last_date && (
                          <div className={`rounded-lg p-2 ${isUrgent ? "bg-red-50" : "bg-slate-50"}`}>
                            <p className={`text-[10px] uppercase font-semibold tracking-wider flex items-center gap-1 ${isUrgent ? "text-red-600" : "text-slate-500"}`}><Calendar size={10} />Last Date</p>
                            <p className={`font-bold mt-0.5 text-sm ${isUrgent ? "text-red-700" : "text-vedanti-navy"}`}>{fmtDate(n.last_date)}</p>
                          </div>
                        )}
                        {n.exam_date && (
                          <div className="bg-slate-50 rounded-lg p-2">
                            <p className="text-[10px] uppercase font-semibold text-slate-500 tracking-wider flex items-center gap-1"><Calendar size={10} />Exam Date</p>
                            <p className="font-bold text-vedanti-navy mt-0.5 text-sm">{fmtDate(n.exam_date) || n.exam_date}</p>
                          </div>
                        )}
                        {n.eligibility_short && (
                          <div className="bg-slate-50 rounded-lg p-2">
                            <p className="text-[10px] uppercase font-semibold text-slate-500 tracking-wider flex items-center gap-1"><GraduationCap size={10} />Eligibility</p>
                            <p className="font-bold text-vedanti-navy mt-0.5 text-sm">{n.eligibility_short}</p>
                          </div>
                        )}
                      </div>

                      <div className="flex flex-wrap gap-2 pt-3 border-t border-slate-100">
                        <Link
                          to={`/notifications/${n.slug || n.id}`}
                          className="flex-1 inline-flex items-center justify-center gap-1.5 bg-vedanti-navy hover:bg-blue-900 text-white text-xs font-bold py-2.5 px-3 rounded-lg transition-all"
                          data-testid={`notification-learn-more-${n.id}`}
                        >
                          Learn More <ArrowRight size={12} />
                        </Link>
                        {n.apply_url && (
                          <a
                            href={n.apply_url}
                            target="_blank"
                            rel="noopener noreferrer nofollow"
                            className="flex-1 inline-flex items-center justify-center gap-1.5 bg-gradient-to-r from-vedanti-orange to-amber-500 text-white text-xs font-bold py-2.5 px-3 rounded-lg hover:-translate-y-0.5 transition-all shadow-sm"
                            data-testid={`notification-apply-${n.id}`}
                          >
                            Apply Now <ExternalLink size={12} />
                          </a>
                        )}
                        {n.course_slug && (
                          <Link
                            to={`/courses/${n.course_slug}`}
                            className="flex-1 inline-flex items-center justify-center gap-1.5 bg-slate-100 hover:bg-slate-200 text-vedanti-navy text-xs font-bold py-2.5 px-3 rounded-lg transition-all"
                          >
                            Coaching <ArrowRight size={12} />
                          </Link>
                        )}
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
          )}

          <p className="text-center text-[11px] text-slate-400 mt-10 flex items-center justify-center gap-2">
            <MapPin size={11} /> Notifications auto-refresh every 12 hours · Powered by Vedanti Academy
          </p>
        </div>
      </section>
    </div>
  );
}
